import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { DataTable } from '@/components/ui/DataTable';
import { AlertTriangle, CheckCircle, RefreshCw, Search, TrendingUp, TrendingDown, Calendar } from 'lucide-react';
import { useErrorMonitoring, useErrorStats, useErrorDetails, useResolveError, useReopenError, ApplicationError } from '@/hooks/useErrorMonitoring';
import { format } from 'date-fns';
import { LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

export default function ErrorMonitoringPage() {
  const [filters, setFilters] = useState({
    severity: 'all',
    errorType: 'all',
    resolved: false,
    search: '',
    category: 'all'
  });
  const [selectedErrorId, setSelectedErrorId] = useState<string | null>(null);
  const [resolutionNotes, setResolutionNotes] = useState('');

  // Filter errors based on category
  const { data: allErrors, isLoading, refetch } = useErrorMonitoring({
    severity: filters.severity === 'all' ? undefined : filters.severity,
    errorType: filters.errorType === 'all' ? undefined : filters.errorType,
    resolved: filters.resolved,
    search: filters.search,
  });

  // Apply category filter on frontend since it's based on message content
  const errors = allErrors?.filter((error: ApplicationError) => {
    if (filters.category === 'all') return true;
    if (filters.category === 'po_creation') {
      return error.message?.toLowerCase().includes('po creation') || 
             error.message?.toLowerCase().includes('purchase order');
    }
    return error.error_category === filters.category;
  });
  const { data: stats } = useErrorStats();
  const resolveError = useResolveError();
  const reopenError = useReopenError();

  const getSeverityBadge = (severity?: string) => {
    const variants = {
      low: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
      medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
      high: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
      critical: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
      unknown: 'bg-muted text-foreground'
    } as const;
    const key = (severity as keyof typeof variants) || 'unknown';
    const label = severity ? severity.toUpperCase() : 'UNKNOWN';
    return <Badge className={variants[key] || variants.unknown}>{label}</Badge>;
  };

  const getTypeBadge = (type?: string) => {
    const variants = {
      frontend: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
      backend: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
      edge_function: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300',
      database: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300',
      unknown: 'bg-muted text-foreground'
    } as const;
    const key = (type as keyof typeof variants) || 'unknown';
    const label = type ? type.replace('_', ' ').toUpperCase() : 'UNKNOWN';
    return <Badge className={variants[key] || variants.unknown}>{label}</Badge>;
  };

  const handleResolve = async (errorId: string) => {
    await resolveError.mutateAsync({ errorId, notes: resolutionNotes });
    setSelectedErrorId(null);
    setResolutionNotes('');
  };

  const columns: any[] = [
    {
      key: 'error_id',
      label: 'Error ID',
      render: (_: any, row: ApplicationError) => {
        const id = row?.error_id || row?.id || '';
        return <code className="text-xs bg-muted px-2 py-1 rounded">{id ? String(id).substring(0, 8) : '—'}</code>;
      }
    },
    {
      key: 'message',
      label: 'Message',
      render: (_: any, row: ApplicationError) => (
        <div className="max-w-md truncate" title={row?.message || ''}>{row?.message || '—'}</div>
      )
    },
    {
      key: 'error_type',
      label: 'Type',
      render: (_: any, row: ApplicationError) => getTypeBadge(row?.error_type)
    },
    {
      key: 'severity',
      label: 'Severity',
      render: (_: any, row: ApplicationError) => getSeverityBadge(row?.severity)
    },
    {
      key: 'occurrence_count',
      label: 'Occurrences',
      render: (_: any, row: ApplicationError) => (
        <span className="font-semibold">{row?.occurrence_count ?? 0}</span>
      )
    },
    {
      key: 'last_occurrence',
      label: 'Last Seen',
      render: (_: any, row: ApplicationError) => {
        const ts = row?.last_occurrence;
        if (!ts) return '—';
        const d = new Date(ts);
        return isNaN(d.getTime()) ? '—' : format(d, 'MMM dd, HH:mm');
      }
    },
    {
      key: 'resolved',
      label: 'Status',
      render: (_: any, row: ApplicationError) => row?.resolved ? (
        <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
          <CheckCircle className="w-3 h-3 mr-1" />
          Resolved
        </Badge>
      ) : (
        <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300">
          <AlertTriangle className="w-3 h-3 mr-1" />
          Active
        </Badge>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_: any, row: ApplicationError) => {
        if (!row) return <span>—</span>;
        return (
          <ErrorDetailsDialog
            error={row}
            onResolve={handleResolve}
            onReopen={() => reopenError.mutate(row.error_id)}
          />
        );
      }
    }
  ];

  const pieColors = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#a4de6c'];

  const severityData = stats ? Object.entries(stats.errorsBySeverity).map(([name, value]) => ({ name, value })) : [];
  const typeData = stats ? Object.entries(stats.errorsByType).map(([name, value]) => ({ name, value })) : [];

  return (
    <div className="container mx-auto py-8 px-4 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Error Monitoring</h1>
          <p className="text-muted-foreground">Track and resolve application errors</p>
        </div>
        <Button onClick={() => refetch()} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Total Errors</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalErrors || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">All time</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Unresolved</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{stats?.unresolvedErrors || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Needs attention</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Critical Errors</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{stats?.criticalErrors || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">High priority</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Last 24h</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.errorRate24h || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Recent errors</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Errors by Severity</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={severityData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={60} label>
                  {severityData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Errors by Type</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={typeData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={60} label>
                  {typeData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Error List</CardTitle>
          <CardDescription>Filter and search through application errors</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search errors..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-9"
              />
            </div>

            <Select value={filters.severity} onValueChange={(value) => setFilters({ ...filters, severity: value })}>
              <SelectTrigger>
                <SelectValue placeholder="All Severities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severities</SelectItem>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filters.errorType} onValueChange={(value) => setFilters({ ...filters, errorType: value })}>
              <SelectTrigger>
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="frontend">Frontend</SelectItem>
                <SelectItem value="backend">Backend</SelectItem>
                <SelectItem value="edge_function">Edge Function</SelectItem>
                <SelectItem value="database">Database</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filters.category} onValueChange={(value) => setFilters({ ...filters, category: value })}>
              <SelectTrigger>
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="po_creation">PO Creation</SelectItem>
                <SelectItem value="payment">Payment</SelectItem>
                <SelectItem value="runtime">Runtime</SelectItem>
                <SelectItem value="validation">Validation</SelectItem>
                <SelectItem value="network">Network</SelectItem>
                <SelectItem value="auth">Auth</SelectItem>
              </SelectContent>
            </Select>

            <Button
              variant={filters.resolved ? 'default' : 'outline'}
              onClick={() => setFilters({ ...filters, resolved: !filters.resolved })}
            >
              {filters.resolved ? 'Show Unresolved' : 'Show Resolved'}
            </Button>
          </div>

          {isLoading ? (
            <div className="text-center py-8">Loading errors...</div>
          ) : (
            <DataTable columns={columns} data={(errors || []).filter(Boolean)} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ErrorDetailsDialog({ 
  error, 
  onResolve, 
  onReopen 
}: { 
  error: ApplicationError; 
  onResolve: (errorId: string) => void;
  onReopen: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const { data } = useErrorDetails(error.error_id);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">View Details</Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Error Details</DialogTitle>
          <DialogDescription>
            Error ID: <code className="bg-muted px-2 py-1 rounded">{error.error_id}</code>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <h3 className="font-semibold mb-2">Error Information</h3>
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-muted-foreground">Type</Label>
                  <p className="text-sm">{error.error_type}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Category</Label>
                  <p className="text-sm">{error.error_category}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Severity</Label>
                  <p className="text-sm">{error.severity}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Occurrences</Label>
                  <p className="text-sm font-semibold">{error.occurrence_count}</p>
                </div>
              </div>

              <div>
                <Label className="text-xs text-muted-foreground">Message</Label>
                <p className="text-sm bg-muted p-2 rounded">{error.message}</p>
              </div>

              {error.stack_trace && (
                <div>
                  <Label className="text-xs text-muted-foreground">Stack Trace</Label>
                  <pre className="text-xs bg-muted p-2 rounded overflow-x-auto max-h-40">{error.stack_trace}</pre>
                </div>
              )}

              {error.component_path && (
                <div>
                  <Label className="text-xs text-muted-foreground">Component</Label>
                  <p className="text-sm font-mono">{error.component_path}</p>
                </div>
              )}

              {error.url && (
                <div>
                  <Label className="text-xs text-muted-foreground">URL</Label>
                  <p className="text-sm font-mono truncate">{error.url}</p>
                </div>
              )}
            </div>
          </div>

          {data?.occurrences && data.occurrences.length > 0 && (
            <div>
              <h3 className="font-semibold mb-2">Recent Occurrences ({data.occurrences.length})</h3>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {data.occurrences.slice(0, 10).map((occurrence) => (
                  <div key={occurrence.id} className="text-xs bg-muted p-2 rounded">
                    <span className="text-muted-foreground">
                      {format(new Date(occurrence.occurred_at), 'MMM dd, yyyy HH:mm:ss')}
                    </span>
                    {occurrence.user_id && <span className="ml-2">User: {occurrence.user_id.substring(0, 8)}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {!error.resolved ? (
            <div>
              <Label htmlFor="resolution-notes">Resolution Notes</Label>
              <Textarea
                id="resolution-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Describe how this error was resolved..."
                className="mt-2"
              />
              <Button 
                onClick={() => {
                  onResolve(error.error_id);
                  setOpen(false);
                }} 
                className="mt-2 w-full"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Mark as Resolved
              </Button>
            </div>
          ) : (
            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded">
              <p className="text-sm font-semibold text-green-800 dark:text-green-300">Resolved</p>
              {error.resolution_notes && (
                <p className="text-sm text-green-700 dark:text-green-400 mt-1">{error.resolution_notes}</p>
              )}
              {error.resolved_at && (
                <p className="text-xs text-green-600 dark:text-green-500 mt-1">
                  Resolved on {format(new Date(error.resolved_at), 'MMM dd, yyyy HH:mm')}
                </p>
              )}
              <Button 
                onClick={() => {
                  onReopen();
                  setOpen(false);
                }} 
                variant="outline"
                size="sm"
                className="mt-2"
              >
                Reopen Error
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
